import type {
  GroupBookingAPIResponse,
  GroupBookingBusPointsAPIResponse,
} from '../infrastructure/types'
import type { Locale } from '@/intl/types'
import defaultFetch from 'isomorphic-unfetch'
import { GroupBookingBusPointsData, GroupBookingData } from '../domain/types'
import { busPointsMapper } from '../infrastructure/mappers/busPointsMapper'
import { groupBookingMapper } from './mappers/groupResponsibleMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
}

interface AssignBusPayload {
  masterBookingNumber: number
  publicationCode: string
  participantsIds: ParticipantsForBusRequest[]
  pickupPointId: string
  returnPointId: string
}

interface ParticipantsForBusRequest {
  bookingCustomerId: number
  childBookingNumber: number | undefined
}

interface AssignBusResponse {
  isSuccess: boolean
}

interface SwapRoomAssignmentsResponse {
  isSuccess: boolean
  errorMessage: string
}

interface AssignRoomSwapPayload {
  masterBookingNumber: string
  publicationCode: string
  targetRooms: Array<{
    bookingNumber: string
    assignedBookingCustomerIds: number[]
  }>
}

function createGroupResponsibleAPIClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
  } = dependencies as Dependencies

  async function getGroupBookingData(masterBookingNumber: string, locale?: Locale): Promise<GroupBookingData> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/GroupBookingsApi/GetGroupBooking/?masterBookingNumber=${masterBookingNumber}&forceLiveData=true`,
    )
    const data: GroupBookingAPIResponse = await response.json()
    return groupBookingMapper(data, locale)
  }

  async function getGroupBookingBusPoints(masterBookingNumber: string): Promise<GroupBookingBusPointsData> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/GroupBookingsApi/GetBusPickupPoints/?masterBookingNumber=${masterBookingNumber}`,
    )

    const data: GroupBookingBusPointsAPIResponse | null = await response.json()
    if (data != null && !data.isSuccess) {
      throw new Error('Failed to fetch bus points')
    }
    return busPointsMapper(data)
  }

  async function assignBusToParticipants(payload: AssignBusPayload): Promise<AssignBusResponse> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/GroupBookingsApi/AssignBus`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    )
    const responseData: AssignBusResponse = await response.json()
    if (!responseData.isSuccess) {
      throw new Error('Failed to assign bus to participants')
    }
    return responseData
  }

  async function assignRoomsViaSwap(payload: AssignRoomSwapPayload): Promise<void> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/GroupBookingsApi/SwapRoomAssignments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    )
    if (!response.ok) {
      throw new Error('Failed to swap room assignments')
    }
    const responseData: SwapRoomAssignmentsResponse | null = await response.json()
    if (!responseData?.isSuccess) {
      throw new Error(responseData?.errorMessage || 'Failed to swap room assignments')
    }
  }

  return {
    getGroupBookingData,
    getGroupBookingBusPoints,
    assignBusToParticipants,
    assignRoomsViaSwap,
  }
}

export { createGroupResponsibleAPIClient }
export default createGroupResponsibleAPIClient()
